import { useEffect, useState } from "react";

import UploadManager from "components/UploadManager";
import AttachmentService from "services/Attachment";

const IN_FLIGHT = new Set(["queued", "uploading"]);

/**
 * New attachment uploads for the form. Files upload to S3 as soon as they are
 * picked, so the save only sends the stored keys. A clone copies the source
 * attachments at save time instead, because the source keeps its own files.
 */
export const useAttachmentUploads = () => {
  const [manager] = useState(() => new UploadManager());
  const [uploads, setUploads] = useState([]);

  // UploadManager reports progress through listeners, outside React.
  useEffect(() => {
    const listenerId = manager.addProgressListener(current =>
      setUploads([...current])
    );
    return () => {
      manager.removeProgressListener(listenerId);
      manager.removeUploads();
    };
  }, [manager]);

  const addFiles = files => {
    // A failed upload is recorded on its entry and shown in the list.
    manager.uploadFiles(files).catch(() => undefined);
  };

  const uploadedAttachments = () =>
    manager.uploads
      .filter(upload => upload.status === "finished")
      .map(upload => upload.attachment);

  const copyAttachments = async attachments => {
    const results = await Promise.allSettled(
      attachments.map(async attachment =>
        AttachmentService.uploadFile(
          await AttachmentService.downloadAsFile(attachment)
        )
      )
    );
    return {
      copied: results
        .filter(result => result.status === "fulfilled")
        .map(result => result.value),
      failedNames: attachments
        .filter((_, index) => results[index].status === "rejected")
        .map(attachment => attachment.file_name),
    };
  };

  return {
    uploads,
    isUploading: uploads.some(upload => IN_FLIGHT.has(upload.status)),
    addFiles,
    removeUpload: manager.removeUpload,
    uploadedAttachments,
    copyAttachments,
  };
};
